import {
  useCallback,
  useEffect,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import { Outlet } from "react-router-dom";
import "./App.css";
import {
  ContentContainer,
  MainContainer,
  MainContentWrapper,
} from "./AppStyles";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import API_ENDPOINTS from "@/util/api";
import api from "./services/axios_custom";
import { useDispatch, useSelector } from "react-redux";
import {
  setLoading,
  setRefLists,
  setRefListItemContexts,
} from "./slices/refListSlice";
import {
  SetReflist,
  SetClientParam,
  SetSecurityRules,
  SetModulesList,
} from "./util/common_function";
import {
  setGlobalClientParam,
  setConfigDefaults,
  setStickyFields,
} from "./slices/configSlice";
import { fetchClientParams } from "./apis/config/clientParamsApi";
import { clearCachedVocabulary } from "./util/vocabularyCache";
import { fetchVocabulary } from "./apis/config/vocabularyApi";
import {
  setUserDetail,
  setUserDetailLoading,
  setUserSecurityUpdated,
} from "./slices/userDetailDetail";
import { fetchLoginStaffById } from "./apis/security/userDetailApi";
import {
  fetchStaffListAllActive,
  fetchJudgeListAll,
} from "./apis/staff/staffApi";
import useAutoLogout from "./hooks/useAutoLogout";
import useSuperSearchShortcut from "./hooks/useSuperSearchShortcut";
import useFacilityLogShortcut from "./hooks/useFacilityLogShortcut";
import useDialog from "./hooks/useDialog";
import { useClientParamBool } from "./hooks/useClientParam";
import Modal from "./common/elements/Modal";
import SuperSearchModal from "./components/SuperSearch";
// Lazy-loaded (same as routes.jsx's page-level splits): FacilityLogInformation
// pulls in the whole Bookings page as a dependency, so importing it eagerly
// here would land that in App.jsx's main bundle for every user, not just
// the ones who hit Alt+L.
const FacilityLogInformation = lazy(
  () => import("./pages/FacilityLog/FacilityLogInformation"),
);
import InternetAlert from "./components/InternetAlert";
import { hasCheckSecurity, isNonEmptyVariable } from "./util/common_function";
import {
  Arbitrary_Names,
  Entity_types,
  Security_Types,
} from "./util/permission_bits";
import { LABELS_CAPITAL } from "./util/usePermissionMask";
import { MODULES_LIST_WITH_ID } from "./util/common_variable";
import {
  loadCachedRefLists,
  saveCachedRefLists,
  clearCachedRefLists,
} from "./util/refListCache";
import {
  loadCachedStaffList,
  saveCachedStaffList,
  clearCachedStaffList,
} from "./util/staffCache";
import { setActiveList } from "./slices/staff/staffSlice";

const SIDEBAR_HIDDEN_ROUTES = ["/dashboard"];

// Mirrors SCOPE.INMATE in pages/FacilityLog/FacilityLogInformation.jsx.
// Duplicated as a plain constant (rather than importing the named SCOPE
// export) because that value is needed before the lazy chunk loads.
const FACILITY_LOG_SCOPE_INMATE = 54280003;

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { showDialog } = useDialog();
  const globalClientParam = useSelector((state) => state?.clientParams?.list);
  const [isSidebarHiddenForRoute, setIsSidebarHiddenForRoute] = useState(false);
  const [headerHeight, setHeaderHeight] = useState();
  const [, setClientParamLoading] = useState(false);
  const [reflist] = useState({
    searchType: "ALL",
    searchValue: [],
  });
  const [sessionTimeout, setSessionTimeout] = useState(240);
  const [superSearchOpen, setSuperSearchOpen] = useState(false);
  const [facilityLogOpen, setFacilityLogOpen] = useState(false);
  const [facilityLogAutoContext, setFacilityLogAutoContext] = useState(null);
  const currentBooking = useSelector((state) => state?.bookings?.bookings);
  // Whether a specific booking's detail view (as opposed to the Bookings
  // list/search screen) is actually on screen right now. bookings.bookings
  // is NOT reliably cleared on navigating back to the list (see
  // pages/Bookings/index.jsx's commented-out setBookingsNull() calls), so it
  // can still hold the last-viewed booking after returning to the list -
  // showBookings is the flag Bookings/index.jsx itself uses to decide
  // whether <BookingsDetails> is rendered, so it's the reliable signal here.
  const isViewingBookingDetail = useSelector(
    (state) => state?.bookings?.showBookings,
  );
  // ASP's AddCommentLogEntry() lets a comment be added for a released
  // inmate (not currently In_Jail) only when this client param is set —
  // see ContainerCommon.vbs: StructurePane().GetClientParam("Bookings",
  // "AllowLogForReleasedInmates", "False").
  const allowLogForReleasedInmates = useClientParamBool(
    "Bookings",
    "AllowLogForReleasedInmates",
    false,
  );

  const headerRef = useRef();
  const mainContainerRef = useRef();

  useEffect(() => {
    globalClientParam?.find((param) => {
      if (
        param.paramContext === "sessionTimeOut" &&
        param.paramName === "Globals"
      ) {
        setSessionTimeout(Number(param.paramValue)); // convert ms to minutes
      }
    });
  }, [globalClientParam]);

  useEffect(() => {
    const sidebarVisible = SIDEBAR_HIDDEN_ROUTES.includes(
      location.pathname.toLowerCase(),
    );
    const isConfigPath = location.pathname.startsWith("/config");
    if (isConfigPath) {
      setIsSidebarHiddenForRoute(true);
    } else {
      setIsSidebarHiddenForRoute(sidebarVisible);
    }
  }, [location?.pathname]);

  const handleLogout = useCallback(async () => {
    try {
      await api.get(API_ENDPOINTS.logout);
    } catch (error) {
      console.error("Logout API failed:", error);
    }

    localStorage.removeItem("allowedMenu");
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("countyLogo");

    sessionStorage.clear();
    clearCachedRefLists();
    clearCachedStaffList();
    clearCachedVocabulary();

    window.location.href = "/login";
  }, []);

  // Auto logout after period of inactivity (warns via Modal before logging out)
  const { showWarning, countdown, setShowWarning, resetTimer } = useAutoLogout(
    handleLogout,
    sessionTimeout,
    300,
  );

  // ASP's UtilKeyboardCommon.asp gates Ctrl-L/S/E behind an arbitrary
  // security function named "CanIPerformShortCut" (see
  // ContainerCommon.vbs/IsThisArbitaryFunctionSetLocal — added for Anoka,
  // crn 3614, to let that client disable the shortcuts for certain users).
  // hasCheckSecurity has no configured rule for most client DBs, which
  // defaults to allow (see the Arbitrary_Names.CanIPerformShortCut comment
  // in permission_bits.js) — so this stays a no-op everywhere except a
  // client that has explicitly restricted it.
  const canUseKeyboardShortcut = () =>
    hasCheckSecurity(
      Security_Types.functionality,
      Entity_types.arbitary,
      LABELS_CAPITAL.VIEW,
      Arbitrary_Names.CanIPerformShortCut,
    );

  // Alt+S "Super Search" — jacket lookup by name, available from anywhere
  // in the app (see hooks/useSuperSearchShortcut.js; the ASP original was
  // Ctrl+S). Suppressed while the session-timeout warning is up so it
  // doesn't stack another modal on top of that one.
  useSuperSearchShortcut(
    () => {
      if (!canUseKeyboardShortcut()) return;
      setSuperSearchOpen(true);
    },
    { enabled: !showWarning },
  );

  const closeFacilityLogShortcutModal = useCallback(() => {
    setFacilityLogOpen(false);
    setFacilityLogAutoContext(null);
  }, []);

  // Alt+L "Add Comment Log Entry" (ASP original: Ctrl+L, see
  // UtilKeyboardCommon.asp case 76 -> ContainerCommon.vbs/
  // AddCommentLogEntry). Mirrors that function's gates, in the same order:
  //   1. arbitrary shortcut permission (CanIPerformShortCut)
  //   2. Facility Log module ADD rights — denial shows a popup dialog (not
  //      a toast), matching ASP's msgbox.
  //   3. Bookings-context behavior: when a booking/inmate detail view is
  //      currently on screen (any module — see isViewingBookingDetail
  //      above, not just the Bookings page), agency is checked BEFORE
  //      in-jail status, exactly like ASP: a mismatch blocks the whole
  //      operation with a popup and the entry form never opens. Only once
  //      agency matches does in-jail / AllowLogForReleasedInmates decide
  //      whether the entry opens pre-scoped to that inmate (booking
  //      currently in jail, or released but the client param allows it) or
  //      falls back to a blank Facility-scoped entry (agency matches, not
  //      in jail, param disallows — silent fallback, matches ASP: that
  //      case just leaves FieldValuesArray empty, no error).
  const openFacilityLogFromShortcut = useCallback(() => {
    if (!canUseKeyboardShortcut()) return;

    const canAddFacilityLog = hasCheckSecurity(
      Security_Types.functionality,
      Entity_types.module,
      LABELS_CAPITAL.ADD,
      MODULES_LIST_WITH_ID.MODULE_FACILITYLOG,
    );
    if (!canAddFacilityLog) {
      showDialog("error", {
        title: "Access Denied",
        content: "You do not have rights to add a log entry.",
        confirmButtonText: "OK",
      });
      return;
    }

    // Not restricted to the Bookings page - Inmate Movement, Staffing,
    // Users, Warrants, VisitorLog and DocumentControl all reuse this same
    // global booking-detail state to show an inmate's record.
    const inBookingContext =
      isViewingBookingDetail && isNonEmptyVariable(currentBooking?.bookingId);

    if (inBookingContext) {
      const loginUser = JSON.parse(localStorage.getItem("userDetails"));
      const userAgency = loginUser?.staff?.organization?.[0]?.agency;
      const arrest = currentBooking?.arrestReports?.[0] || {};
      const inmateAgency = arrest?.housingAgency;

      // Sugar 3625 in the ASP original — checked regardless of in-jail
      // status, and blocks the entry entirely (no modal) on a mismatch.
      if (
        isNonEmptyVariable(userAgency) &&
        isNonEmptyVariable(inmateAgency) &&
        userAgency !== inmateAgency
      ) {
        showDialog("error", {
          title: "Access Denied",
          content:
            "You cannot add a comment entry for an inmate outside of your agency.",
          confirmButtonText: "OK",
        });
        return;
      }

      const inJail = currentBooking?.inJail === true;
      if (inJail || allowLogForReleasedInmates) {
        const bio = currentBooking?.bio?.[0] || {};
        setFacilityLogAutoContext({
          initialScope: FACILITY_LOG_SCOPE_INMATE,
          inmateBookingDetail: {
            bookingID: currentBooking?.bookingId,
            bookingNumber: currentBooking?.bookingNumber,
            lname: bio?.lName,
            fname: bio?.fName,
            mname: bio?.mName,
            arrestDate: arrest?.arrestDate,
            dob: bio?.dob,
            pinNo: bio?.pinNo,
            facilityStatus: arrest?.workhouseStatusId,
            assignedLocation: arrest?.currentLocation,
            currentLocation: arrest?.currentLocation,
            classification: arrest?.classificationId,
          },
        });
      } else {
        setFacilityLogAutoContext(null);
      }
    } else {
      setFacilityLogAutoContext(null);
    }

    setFacilityLogOpen(true);
  }, [
    currentBooking,
    isViewingBookingDetail,
    allowLogForReleasedInmates,
    showDialog,
  ]);

  // Alt+Shift+L "View Facility Log" (ASP original: Ctrl+Shift+L, see
  // UtilKeyboardCommon.asp case 76's shiftKey branch -> FireEvent
  // EVENT_FACILITYVIEWER_DEFAULTSTRING -> ContainerHosted.vbs ->
  // DisplayCommentLog()). ASP gates this the same as plain Ctrl+L
  // (CanIPerformShortCut) but does NOT run AddCommentLogEntry()'s
  // ADD-rights/agency/in-jail checks — those only apply to adding an
  // entry, not viewing the log. The React app's Facility Log list page
  // already gates its own content behind the viewer's own VIEW/FIND
  // rights, so navigating there is enough.
  const openFacilityLogViewer = useCallback(() => {
    if (!canUseKeyboardShortcut()) return;
    navigate("/facility-log");
  }, [navigate]);

  useFacilityLogShortcut(
    ({ shiftKey }) => {
      if (shiftKey) {
        openFacilityLogViewer();
      } else {
        openFacilityLogFromShortcut();
      }
    },
    { enabled: !showWarning && !facilityLogOpen },
  );

  useEffect(() => {
    setHeaderHeight(headerRef?.current?.clientHeight);
  }, [headerRef]);

  const refListinfo = JSON.stringify(reflist);

  useEffect(() => {
    getLoginStaffDetails();
    setClientParamLoading(true);
    getAllClientParams();
    hydrateRefListFromCache();
    hydrateStaffListFromCache();
    getRefList();
    getStaffList();
    getJudgeList();
    getRefListItemContexts();
  }, []);

  useEffect(() => {
    api
      .get(API_ENDPOINTS.config.configDefaults.getAll)
      .then((res) => dispatch(setConfigDefaults(res.data)))
      .catch((err) => console.error("config defaults:", err));
  }, []);

  // Load the ConfigStickyFields definitions once at startup (same pattern as
  // configDefaults above). Screens look these up via useStickyFieldNames()
  // instead of each making their own network call.
  useEffect(() => {
    api
      .get(API_ENDPOINTS.config.stickyFields.getAll)
      .then((res) => dispatch(setStickyFields(res.data)))
      .catch((err) => console.error("sticky fields:", err));
  }, []);

  // Site label renames (legacy Vocabulary table). App mounts once per page
  // load behind AuthGuard, so this runs right after login and on every
  // refresh — new rows show on the next refresh, as in PCI. Labels render
  // from the cached copy meanwhile, so there is no flash of default text.
  useEffect(() => {
    dispatch(fetchVocabulary());
  }, []);

  const getAllClientParams = () => {
    dispatch(fetchClientParams());
    api
      .get(API_ENDPOINTS.clientParams)
      .then((response) => {
        dispatch(setGlobalClientParam(response?.data));
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message,
        );
      });
  };

  // Serve the last-fetched refList payload from IndexedDB right away (so
  // dropdowns render with data instead of sitting in a loading state on
  // reload), then let getRefList's API call replace it with the latest data
  // once the response (6MB+) comes back.
  const hydrateRefListFromCache = () => {
    loadCachedRefLists().then((cached) => {
      if (cached) {
        dispatch(setRefLists(cached));
        dispatch(setLoading(false));
      }
    });
  };

  const hydrateStaffListFromCache = () => {
    loadCachedStaffList().then((cached) => {
      if (cached) {
        dispatch(setActiveList(cached));
      }
    });
  };

  const getRefList = (url = API_ENDPOINTS.refList) => {
    api
      .post(url, refListinfo)
      .then((response) => {
        dispatch(setRefLists(response?.data));
        dispatch(setLoading(false));
        saveCachedRefLists(response?.data);
        return response?.data;
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message,
        );
      });
  };
  const getStaffList = () => {
    dispatch(fetchStaffListAllActive())
      .unwrap()
      .then((data) => saveCachedStaffList(data))
      .catch((error) => console.error("Request failed:", error));
  };
  const getJudgeList = () => {
    dispatch(fetchJudgeListAll())
      .unwrap()
      .catch((error) => console.error("Request failed:", error));
  };

  const getRefListItemContexts = async (
    url = API_ENDPOINTS.refListItemContexts,
  ) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(url);
      dispatch(setRefListItemContexts(response?.data));
      return response?.data;
    } catch (error) {
      console.error(
        "Request failed:",
        error.response ? error.response.data : error.message,
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const getLoginStaffDetails = () => {
    const loginUser = JSON.parse(localStorage.getItem("userDetails"));
    dispatch(setUserDetailLoading(true));
    dispatch(setUserSecurityUpdated(false));
    dispatch(fetchLoginStaffById(loginUser?.staffId)).then((response) => {
      const { payload } = response;
      const result = processStaffGroupData(payload);
      dispatch(setUserDetail({ ...loginUser, staffGroup: result }));
      dispatch(setUserDetailLoading(false));
    });
  };

  function processStaffGroupData(staffGroup) {
    const combinedData = {
      groups: [],
      securityRulesCriteria: [],
      securityRulesFunctionality: [],
      securityRulesRecords: [],
      clientParams: [],
    };

    staffGroup?.forEach((group) => {
      // Combine security group
      combinedData?.groups?.push({
        groupName: group?.groupName,
        groupId: group?.staffGroupsID,
      });

      // Combine security rules criteria
      group?.securityRulesCriteria?.forEach((criteria) => {
        const existing = combinedData?.securityRulesCriteria.find(
          (c) =>
            c?.securityRulesCriteriaID === criteria?.securityRulesCriteriaID,
        );
        if (existing) {
          existing.allowSetting = Math.max(
            existing?.allowSetting,
            criteria?.allowSetting,
          );
          existing.denySetting = Math.max(
            existing?.denySetting,
            criteria?.denySetting,
          );
        } else {
          combinedData?.securityRulesCriteria.push({ ...criteria });
        }
      });

      // Combine security rules functionality
      group?.securityRulesFunctionality?.forEach((func) => {
        const existing = combinedData?.securityRulesFunctionality?.find(
          (f) =>
            f?.securityRulesFunctionalityID ===
            func?.securityRulesFunctionalityID,
        );
        if (existing) {
          existing.allowSetting = Math.max(
            existing?.allowSetting,
            func?.allowSetting,
          );
          existing.denySetting = Math.max(
            existing?.denySetting,
            func?.denySetting,
          );
        } else {
          combinedData?.securityRulesFunctionality.push({ ...func });
        }
      });

      // Combine security rules records
      group?.securityRulesRecords?.forEach((record) => {
        const existing = combinedData?.securityRulesRecords?.find(
          (r) => r?.securityRulesRecordsID === record?.securityRulesRecordsID,
        );
        if (existing) {
          existing.allowSetting = Math.max(
            existing?.allowSetting,
            record?.allowSetting,
          );
          existing.denySetting = Math.max(
            existing?.denySetting,
            record?.denySetting,
          );
        } else {
          combinedData?.securityRulesRecords.push({ ...record });
        }
      });

      // Combine client params
      group.clientParams.forEach((param) => {
        const existing = combinedData?.clientParams?.find(
          (p) => p.clientParamID === param?.clientParamID,
        );
        if (!existing) {
          combinedData?.clientParams?.push({ ...param });
        }
      });
    });

    return combinedData;
  }

  const closeModal = () => {
    resetTimer();
    setShowWarning(false);
  };

  return (
    <>
      <InternetAlert />
      <SetReflist></SetReflist>
      <SetClientParam />
      <SetSecurityRules />
      <SetModulesList />
      <MainContainer ref={mainContainerRef}>
        <Header ref={headerRef} />
        <MainContentWrapper
          align_items={"stretch"}
          className="main-content-flex"
          gap={"20px"}
          headerHeight={headerHeight}
        >
          {!isSidebarHiddenForRoute && <Sidebar headerHeight={headerHeight} />}
          <ContentContainer headerHeight={headerHeight}>
            <Outlet context={{ headerHeight }} />
          </ContentContainer>
        </MainContentWrapper>
      </MainContainer>

      {showWarning && (
        <Modal
          header={"⚠️ Session Expiring Soon"}
          show={showWarning}
          size="md"
          onHide={closeModal}
          handleSubmit={closeModal}
          handleCancel={handleLogout}
          footerCloseActionText="Logout Now"
          footerSubmitButtonText="Continue Session"
          footerActionsNeed={false}
        >
          <p>You’ll be logged out in {countdown} seconds due to inactivity.</p>
          <p>To continue press any key </p>
        </Modal>
      )}

      <SuperSearchModal
        show={superSearchOpen}
        onHide={() => setSuperSearchOpen(false)}
      />

      {facilityLogOpen && (
        <Modal
          width={"80%"}
          header="Facility Log Information"
          show={facilityLogOpen}
          handleCancel={closeFacilityLogShortcutModal}
          onHide={closeFacilityLogShortcutModal}
          footerActionsNeed={false}
        >
          <Suspense fallback={null}>
            <FacilityLogInformation
              handleCancel={closeFacilityLogShortcutModal}
              facilityLogDetail={null}
              inmateBookingDetail={
                facilityLogAutoContext?.inmateBookingDetail || null
              }
              setFacilityLogDetail={() => {}}
              setInmateBookingDetail={() => {}}
              actionMode="modify"
              initialScope={facilityLogAutoContext?.initialScope || null}
            />
          </Suspense>
        </Modal>
      )}
    </>
  );
};

export default App;
